<template>
  <div>
    <a-page-header :title="`执行计划详情 - ${planDetail?.task_id || planDetail?.plan_code || ''}`">
      <template #extra>
        <a-button @click="handleBack">返回</a-button>
      </template>
    </a-page-header>

    <a-card style="margin-top: 16px" v-if="planDetail">
      <a-descriptions :column="3" bordered style="margin-bottom: 16px">
        <a-descriptions-item label="方案名称">{{ planDetail.scheme_name }}</a-descriptions-item>
        <a-descriptions-item label="task_id">{{ planDetail.task_id }}</a-descriptions-item>
        <a-descriptions-item label="user_id">{{ planDetail.user_id || '-' }}</a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="getStatusColor(planDetail.status)">
            {{ getStatusText(planDetail.status) }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="故障现象">{{ planDetail.fault_phenomenon || '-' }}</a-descriptions-item>
        <a-descriptions-item label="SYSTEMID&Name">{{ planDetail.system_display || '-' }}</a-descriptions-item>
        <a-descriptions-item label="创建时间">{{ formatTime(planDetail.create_time) }}</a-descriptions-item>
        <a-descriptions-item label="结束时间">{{ formatTime(planDetail.end_time) }}</a-descriptions-item>
      </a-descriptions>

      <!-- 用户反馈显示 -->
      <a-divider>用户反馈</a-divider>
      <div v-if="userFeedbacks.length === 0" style="text-align: center; padding: 20px; color: #999;">
        没有反馈
      </div>
      <div v-else>
        <div v-for="(feedback, index) in userFeedbacks" :key="feedback.id" 
             :style="{
               marginBottom: index < userFeedbacks.length - 1 ? '12px' : '0',
               padding: '12px',
               borderRadius: '4px',
               backgroundColor: feedback.helpful_status === 1 ? '#f6ffed' : '#fff1f0'
             }">
          <a-descriptions :column="2" size="small" bordered>
            <a-descriptions-item label="反馈用户ID" :span="1">
              {{ feedback.user_id || '匿名用户' }}
            </a-descriptions-item>
            <a-descriptions-item label="是否有帮助" :span="1">
              <a-tag :color="feedback.helpful_status === 1 ? 'green' : 'red'">
                {{ feedback.helpful_status === 1 ? '有帮助' : '无帮助' }}
              </a-tag>
            </a-descriptions-item>
            <a-descriptions-item label="反馈内容" :span="2">
              <div style="white-space: pre-wrap; word-break: break-word;">{{ feedback.feedback_content }}</div>
            </a-descriptions-item>
            <a-descriptions-item label="反馈时间" :span="2">
              {{ formatTime(feedback.create_time) }}
            </a-descriptions-item>
          </a-descriptions>
        </div>
      </div>

      <a-divider>执行记录</a-divider>
      <div style="margin-bottom: 12px;">
        <a-button type="primary" @click="showCreatePlanCaseModal = true" :loading="creatingPlanCase">
          整条加入测试用例
        </a-button>
      </div>

      <!-- 整条加入测试用例确认弹窗 -->
      <a-modal
        v-model:open="showCreatePlanCaseModal"
        title="整条加入测试用例"
        ok-text="创建"
        cancel-text="取消"
        :confirm-loading="creatingPlanCase"
        @ok="handleCreatePlanTestCaseConfirm"
      >
        <a-form layout="vertical" style="margin-top: 8px;">
          <a-form-item>
            <a-checkbox v-model:checked="createPlanCaseOptions.create_snapshot">
              创建快照（为每一步自动创建基线快照，便于后续「知识不变换模型」等测试）
            </a-checkbox>
          </a-form-item>
        </a-form>
      </a-modal>

      <a-table
        :columns="logColumns"
        :data-source="executionLogs"
        :loading="logLoading"
        :pagination="false"
        row-key="id"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'step_name'">
            <div v-if="record.step_level === 1" style="font-weight: bold; background-color: #f5f5f5; padding: 4px 8px;">
              {{ record.step_name }}
            </div>
            <div v-else style="padding-left: 20px;">
              {{ record.step_name }}
            </div>
          </template>
          <template v-else-if="column.key === 'step_order'">
            <span v-if="record.step_level === 1">{{ record.step_order }}</span>
            <span v-else>{{ record.step_order }}.{{ record.sub_step_order }}</span>
          </template>
          <template v-else-if="column.key === 'status'">
            <span v-if="record.step_level === 1">-</span>
            <a-tag v-else :color="getLogStatusColor(record.status)">
              {{ getLogStatusText(record.status) }}
            </a-tag>
          </template>
          <template v-else-if="column.key === 'model'">
            <span v-if="record.step_level === 1">-</span>
            <span v-else>{{ getModelName(record) }}</span>
          </template>
          <template v-else-if="column.key === 'start_time'">
            <span v-if="record.step_level === 1">-</span>
            <span v-else>{{ formatTime(record.start_time) }}</span>
          </template>
          <template v-else-if="column.key === 'end_time'">
            <span v-if="record.step_level === 1">-</span>
            <span v-else>{{ formatTime(record.end_time) }}</span>
          </template>
          <template v-else-if="column.key === 'duration'">
            <span v-if="record.step_level === 1">-</span>
            <span v-else>{{ formatDuration(record.duration) }}</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button v-if="record.step_level === 2" type="link" size="small" @click="handleViewLog(record)">
              查看详情
            </a-button>
            <a-button v-if="record.step_level === 2" type="link" size="small" @click="handleCreateTestCase(record)">
              创建用例
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-spin v-else :spinning="loading" style="width: 100%; margin-top: 50px;">
      <div style="text-align: center; padding: 50px;">
        <a-empty description="加载中..." />
      </div>
    </a-spin>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import dayjs from 'dayjs';
import { getCallPlanById, getCallPlanLogs } from '../api/callPlan';
import { getModels } from '../api/model';
import { getUserFeedbackByTaskId } from '../api/userFeedback';
import { createTestCaseFromPlan } from '../api/evalTestCaseV2';

export default {
  name: 'ExecutionPlanDetail',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(false);
    const logLoading = ref(false);
    const planDetail = ref(null);
    const executionLogs = ref([]);
    const modelList = ref([]);
    const modelMap = ref({}); // { modelId: modelName }
    const userFeedbacks = ref([]); // 用户反馈列表
    const creatingPlanCase = ref(false);
    const showCreatePlanCaseModal = ref(false);
    const createPlanCaseOptions = ref({
      create_snapshot: true
    });

    const logColumns = [
      { title: '步骤名称', key: 'step_name', width: 250 },
      { title: '步骤顺序', key: 'step_order', width: 120 },
      { title: '状态', key: 'status', width: 100 },
      { title: '大模型', key: 'model', width: 180 },
      { title: '开始时间', key: 'start_time', width: 180 },
      { title: '结束时间', key: 'end_time', width: 180 },
      { title: '执行耗时', key: 'duration', width: 120 },
      { title: '操作', key: 'action', width: 200, fixed: 'right' }
    ];

    const getStatusColor = (status) => {
      const colors = {
        pending: 'orange',
        executing: 'blue',
        completed: 'green',
        cancelled: 'default',
        failed: 'red'
      };
      return colors[status] || 'default';
    };

    const getStatusText = (status) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        completed: '已完成',
        cancelled: '已取消',
        failed: '失败'
      };
      return texts[status] || status;
    };

    const getLogStatusColor = (status) => {
      const colors = {
        pending: 'orange',
        executing: 'blue',
        success: 'green',
        failed: 'red',
        skipped: 'default'
      };
      return colors[status] || 'default';
    };

    const getLogStatusText = (status) => {
      const texts = {
        pending: '待执行',
        executing: '执行中',
        success: '成功',
        failed: '失败',
        skipped: '跳过'
      };
      return texts[status] || status;
    };

    const formatTime = (time) => {
      if (!time) return '-';
      return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
    };

    const formatDuration = (duration) => {
      if (!duration) return '-';
      // duration是毫秒，转换为秒（保留1位小数）
      const seconds = (duration / 1000).toFixed(1);
      return `${seconds}s`;
    };

    // 获取模型名称
    const getModelName = (record) => {
      if (!record.request_data || !record.request_data._metadata) {
        return '-';
      }
      const modelId = record.request_data._metadata.modelId;
      if (!modelId) {
        return '-';
      }
      // 从模型映射中获取模型名称
      const model = modelMap.value[modelId];
      if (model) {
        return `${model.model_name || model.model_id || modelId}`;
      }
      // 如果找不到，返回模型ID
      return `模型ID: ${modelId}`;
    };

    // 加载模型列表
    const loadModels = async () => {
      try {
        const res = await getModels();
        if (res.code === 200) {
          // res.data 是一个对象，包含 list 属性（数组）
          const models = res.data?.list || res.data || [];
          modelList.value = Array.isArray(models) ? models : [];
          // 构建模型ID到模型对象的映射
          const map = {};
          modelList.value.forEach(model => {
            map[model.id] = model;
          });
          modelMap.value = map;
        }
      } catch (error) {
        console.error('加载模型列表失败:', error);
      }
    };

    const loadPlanDetail = async () => {
      loading.value = true;
      try {
        const planId = route.params.id;
        const res = await getCallPlanById(planId);
        if (res.code === 200) {
          planDetail.value = res.data;
          
          // 解析engine_info获取SYSTEMID（后端已处理，这里不需要再解析）
          // 如果后端没有处理，则使用后端返回的system_display

          // 从scheme_snapshot中提取方案名称
          if (planDetail.value.scheme_snapshot && planDetail.value.scheme_snapshot.scheme) {
            planDetail.value.scheme_name = planDetail.value.scheme_snapshot.scheme.scheme_name;
          }

          // 加载用户反馈（如果有task_id）
          if (planDetail.value.task_id) {
            loadUserFeedback(planDetail.value.task_id);
          }
        } else {
          message.error(res.message || '加载计划详情失败');
        }
      } catch (error) {
        message.error('加载计划详情失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 加载用户反馈
    const loadUserFeedback = async (taskId) => {
      try {
        const res = await getUserFeedbackByTaskId(taskId);
        if (res.code === 200) {
          userFeedbacks.value = res.data || [];
        } else {
          // 如果第二个数据库未配置，会返回空数组，不显示错误
          if (res.message && !res.message.includes('未配置')) {
            console.warn('加载用户反馈失败:', res.message);
          }
        }
      } catch (error) {
        // 静默处理错误，避免影响主流程
        console.warn('加载用户反馈失败:', error.message);
      }
    };

    const loadExecutionLogs = async () => {
      logLoading.value = true;
      try {
        const planId = route.params.id;
        const res = await getCallPlanLogs(planId);
        if (res.code === 200) {
          // 排序逻辑：
          // 1. 先按 step_order 排序（步骤顺序）
          // 2. 然后按 step_level 排序（步骤在前，Worker在后）
          // 3. 同为 Worker（step_level=2）时按 sub_step_order 升序
          const logs = (res.data || []).sort((a, b) => {
            if (a.step_order !== b.step_order) {
              return a.step_order - b.step_order;
            }
            if (a.step_level !== b.step_level) {
              return a.step_level - b.step_level;
            }
            const aSub = a.sub_step_order ?? 0;
            const bSub = b.sub_step_order ?? 0;
            return aSub - bSub;
          });
          executionLogs.value = logs;
        } else {
          message.error(res.message || '加载执行记录失败');
        }
      } catch (error) {
        message.error('加载执行记录失败');
        console.error(error);
      } finally {
        logLoading.value = false;
      }
    };

    const handleBack = () => {
      window.close();
    };

    const handleViewLog = (record) => {
      // 新标签页打开Worker详情页
      const routeData = router.resolve({
        name: 'execution-log-detail',
        params: { id: record.id }
      });
      window.open(routeData.href, '_blank');
    };

    const handleCreateTestCase = (record) => {
      // 跳转到测试用例管理页面，预填执行日志ID
      router.push({
        path: '/eval-v2/test-cases',
        query: { 
          action: 'create',
          execution_log_id: record.id 
        }
      });
    };

    const handleCreatePlanTestCaseConfirm = async () => {
      const planId = route.params.id;
      if (!planId) {
        message.error('缺少计划ID');
        return;
      }
      creatingPlanCase.value = true;
      try {
        const res = await createTestCaseFromPlan(planId, {
          criteria_a: '完全符合预期，准确率>95%',
          criteria_b: '基本符合预期，准确率>80%',
          criteria_c: '部分符合预期，准确率>60%',
          pass_threshold: 'B',
          create_snapshot: createPlanCaseOptions.value.create_snapshot,
          expected_scope: 'all_steps'
        });
        if (res.code === 200 && res.data && res.data.test_case_id) {
          showCreatePlanCaseModal.value = false;
          message.success('已创建整条计划测试用例');
          const routeData = router.resolve({
            path: `/eval-v2/test-case/${res.data.test_case_id}`
          });
          window.open(routeData.href, '_blank');
        } else {
          message.error(res.message || '创建失败');
        }
      } catch (e) {
        message.error(e.response?.data?.message || e.message || '创建失败');
        console.error(e);
      } finally {
        creatingPlanCase.value = false;
      }
    };

    onMounted(() => {
      loadPlanDetail();
      loadModels();
      loadExecutionLogs();
    });

    return {
      loading,
      logLoading,
      planDetail,
      executionLogs,
      logColumns,
      userFeedbacks,
      getStatusColor,
      getStatusText,
      getLogStatusColor,
      getLogStatusText,
      formatTime,
      formatDuration,
      getModelName,
      handleBack,
      handleViewLog,
      handleCreateTestCase,
      showCreatePlanCaseModal,
      createPlanCaseOptions,
      handleCreatePlanTestCaseConfirm,
      creatingPlanCase
    };
  }
};
</script>

<style scoped>
</style>
